import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Button, Form, Badge, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import { amazonAPI } from '../api/amazonAPI'
import { formatCurrency, formatDate } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { createColumnToggleDropdown, useTableState } from '../utils/tableUtils'
import DailyDashboardGuideModal from '../components/DailyDashboardGuideModal'
import '../styles/selectControl.css'
import '../styles/segmentedControl.css'

const fmtDec = (val, digits = 2) => {
    if (val === null || val === undefined) return ''
    return Number(val).toFixed(digits)
}

const fmtPct = (val) => {
    if (val === null || val === undefined) return ''
    return Number(val).toFixed(1) + '%'
}

const fmtInt = (val) => {
    if (val === null || val === undefined) return ''
    return Number(val).toLocaleString()
}

const CHANNEL_OPTIONS = [
    { value: 'amazon', label: 'Amazon (FBA)' },
    { value: 'seller', label: 'Seller (FBM)' },
    { value: 'both', label: 'Both' },
]
const CHANNEL_LABELS = { amazon: 'FBA', seller: 'FBM', both: 'FBA + FBM' }
const parseChannel = (v) => (['amazon', 'seller', 'both'].includes(v) ? v : 'amazon')

function AmazonDailyDashboardScreen() {
    const navigate = useNavigate()
    const location = useLocation()

    const urlParams = new URLSearchParams(location.state?.returnFilters || location.search)
    // Captured once: the URL-sync effect replaces history entries and drops location.state
    const [backTo] = useState(location.state?.backTo || '')
    const [backLabel] = useState(location.state?.backLabel || '')

    // Get yesterday's date in YYYY-MM-DD format
    const getYesterday = () => {
        const d = new Date()
        d.setDate(d.getDate() - 1)
        return d.toISOString().split('T')[0]
    }

    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState(() => Number(urlParams.get('account')) || '')
    // Empty means "let the backend pick the newest day that has collected data"
    const [selectedDate, setSelectedDate] = useState(() => urlParams.get('date') || '')
    const [error, setError] = useState('')

    const [data, setData] = useState(null)
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [summary, setSummary] = useState(null)

    const [excludeInactive, setExcludeInactive] = useState(true)
    const [inventoryFilter, setInventoryFilter] = useState('')
    // Fulfillment channel for units/revenue; the team reads FBA by default
    const [channel, setChannel] = useState(() => parseChannel(urlParams.get('channel')))
    const [searchTerm, setSearchTerm] = useState(() => urlParams.get('search') || '')
    const [searchInputValue, setSearchInputValue] = useState(() => urlParams.get('search') || '')
    const debounceTimeoutRef = useRef(null)
    const [sortColumn, setSortColumn] = useState(() => urlParams.get('sortCol') || 'revenue_mtd')
    const [sortDirection, setSortDirection] = useState(() => urlParams.get('sortDir') || 'desc')
    const [currentPage, setCurrentPage] = useState(() => parseInt(urlParams.get('page')) || 1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())

    const [hasLoaded, setHasLoaded] = useState(false)
    const [showGuide, setShowGuide] = useState(false)

    const initialTableState = {
        visibility: {
            asin: true, amazon_sku: true, product_name: true,
            sale_price: true, fba_fee: true, referral_fee: true, inbound_fee: true,
            storage_fee: true, cogs: true, profit: true, margin_pct: true,
            break_even_acos: false,
            ad_spend_14d: false, ad_sales_14d: false, acos: true,
            ad_cost_per_unit: true,
            impressions_14d: true, clicks_14d: true, sessions_14d: true,
            conversion_pct: true, share_sales_by_ad_pct: true,
            units_sold_mtd: true, revenue_mtd: true,
            units_1_10d: true, units_11_20d: true, units_21_30d: true,
            inventory_qty: true, available_inventory: true,
            inventory_level: true, sales_level: true,
            health_status: true, low_inventory_fee: false,
        },
        widths: {}
    }
    const { columnState, updateColumnVisibility } = useTableState('amazon_daily_dashboard', initialTableState)
    const columnVisibility = { ...initialTableState.visibility, ...(columnState.visibility || {}) }
    const toggleColumnVisibility = (columnId) => updateColumnVisibility(columnId, !columnVisibility[columnId])
    const adDays = data?.ad_lookback_days || 30
    const columnLabels = {
        asin: 'ASIN', amazon_sku: 'SKU', product_name: 'Product Name',
        sale_price: 'Sale Price', fba_fee: 'FBA Fee', referral_fee: 'Referral Fee',
        inbound_fee: 'Inbound Fee', storage_fee: 'Storage Fee', cogs: 'COGS',
        profit: 'Profit', margin_pct: 'Margin %', break_even_acos: 'BE ACoS %',
        ad_spend_14d: `Ad Spend (${adDays}d)`, ad_sales_14d: `Ad Sales (${adDays}d)`, acos: 'ACoS',
        ad_cost_per_unit: 'Ad Cost/Unit',
        impressions_14d: `Impressions (${adDays}d)`, clicks_14d: `Clicks (${adDays}d)`,
        sessions_14d: `Sessions (${adDays}d)`,
        conversion_pct: 'Conversion %', share_sales_by_ad_pct: 'Share by Ad %',
        units_sold_mtd: 'Units Sold (MTD)', revenue_mtd: 'Revenue (MTD)',
        units_1_10d: 'Units 1-10d', units_11_20d: 'Units 11-20d', units_21_30d: 'Units 21-30d',
        inventory_qty: 'Inventory Qty', available_inventory: 'Available Inv.',
        inventory_level: 'Inventory Level',
        health_status: 'Health Status', low_inventory_fee: 'Low Inv. Fee',
    }

    // Restore filters from returnFilters on mount
    useEffect(() => {
        if (location.state?.returnFilters) {
            navigate(`${location.pathname}${location.state.returnFilters}`, {
                replace: true, state: {}
            })
        }
    }, [])

    // Sync filters to URL
    useEffect(() => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (selectedDate) params.set('date', selectedDate)
        if (searchTerm) params.set('search', searchTerm)
        if (currentPage > 1) params.set('page', currentPage)
        if (sortColumn !== 'revenue_mtd') params.set('sortCol', sortColumn)
        if (sortDirection !== 'desc') params.set('sortDir', sortDirection)
        if (channel !== 'amazon') params.set('channel', channel)
        const search = params.toString()
        navigate(`${location.pathname}${search ? '?' + search : ''}`, { replace: true })
    }, [selectedAccountId, selectedDate, searchTerm, currentPage, sortColumn, sortDirection, channel])

    // Fetch accounts
    useEffect(() => {
        const fetchAccounts = async () => {
            try {
                const data = await amazonAPI.accounts.getAll()
                if (data) {
                    const active = data.filter(a => a.is_active)
                    setAccounts(active)
                    if (active.length === 1 && !selectedAccountId) {
                        setSelectedAccountId(active[0].id)
                    }
                }
            } catch (err) {
                console.error('Failed to fetch accounts:', err)
            }
        }
        fetchAccounts()
    }, [])

    const fetchData = useCallback(async () => {
        if (!selectedAccountId) return
        setLoading(true)
        setError('')
        try {
            const params = {
                account_id: selectedAccountId,
                page: currentPage,
                pageSize: pageSize,
                sortColumn: sortColumn,
                sortDirection: sortDirection,
            }
            if (selectedDate) params.date = selectedDate
            if (searchTerm) params.searchTerm = searchTerm
            params.exclude_inactive = excludeInactive
            if (inventoryFilter) params.inventory_filter = inventoryFilter
            params.channel = channel

            const result = await amazonAPI.dailyDashboard.get(params)
            if (result) {
                setRows(result.results || [])
                setTotalItems(result.count || 0)
                setSummary(result.summary || null)
                setData(result)
                setHasLoaded(true)
                if (!selectedDate && result.date) setSelectedDate(result.date)
            }
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setLoading(false)
        }
    }, [selectedAccountId, selectedDate, currentPage, pageSize, sortColumn, sortDirection, searchTerm, excludeInactive, inventoryFilter, channel])

    const handleLoad = () => {
        setCurrentPage(1)
        fetchData()
    }

    // Load automatically when the page opens with an account in the URL (deep link from an
    // alert's Details button, or a reload of a page that already had filters). Runs once.
    const autoLoadedRef = useRef(false)
    useEffect(() => {
        if (autoLoadedRef.current || hasLoaded) return
        if (new URLSearchParams(location.search).get('account') && selectedAccountId) {
            autoLoadedRef.current = true
            fetchData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccountId])

    // Re-fetch when pagination/sort/search changes (only after initial load)
    useEffect(() => {
        if (hasLoaded) {
            fetchData()
        }
    }, [currentPage, pageSize, sortColumn, sortDirection, searchTerm, excludeInactive, inventoryFilter, channel])

    const handleSearch = (e) => {
        const value = e?.target?.value ?? e
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value)
            setCurrentPage(1)
        }, 300)
    }

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
        setCurrentPage(1)
    }

    const handleSort = (column, direction) => {
        setSortColumn(column)
        setSortDirection(direction)
        setCurrentPage(1)
    }

    const accountOptions = accounts.map(a => ({ value: a.id, label: a.account_name }))

    const dashboardColumns = [
        { id: 'asin', header: 'ASIN', sortable: true,
          cell: (row) => <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={searchTerm} /> },
        { id: 'amazon_sku', header: 'SKU', sortable: true,
          cell: (row) => row.amazon_sku || '' },
        { id: 'product_name', header: 'Product Name', sortable: true,
          cell: (row) => row.product_name || '' },
        { id: 'sale_price', header: 'Sale Price', sortable: true,
          cell: (row) => row.sale_price != null ? `$${fmtDec(row.sale_price)}` : '' },
        { id: 'fba_fee', header: 'FBA Fee', sortable: true,
          cell: (row) => row.fba_fee != null ? `$${fmtDec(row.fba_fee)}` : '' },
        { id: 'referral_fee', header: 'Referral Fee', sortable: true,
          cell: (row) => row.referral_fee != null ? `$${fmtDec(row.referral_fee)}` : '' },
        { id: 'inbound_fee', header: 'Inbound Fee', sortable: true,
          cell: (row) => row.inbound_fee != null ? `$${fmtDec(row.inbound_fee)}` : '' },
        { id: 'storage_fee', header: 'Storage Fee', sortable: true,
          cell: (row) => row.storage_fee != null ? `$${fmtDec(row.storage_fee)}` : '' },
        { id: 'cogs', header: 'COGS', sortable: true,
          cell: (row) => row.cogs != null ? `$${fmtDec(row.cogs)}` : '' },
        { id: 'profit', header: 'Profit', sortable: true,
          cell: (row) => {
              if (row.profit === null || row.profit === undefined) return ''
              const val = Number(row.profit)
              const color = val >= 0 ? 'text-success' : 'text-danger'
              return <span className={color}>${fmtDec(val)}</span>
          }},
        { id: 'margin_pct', header: 'Margin %', sortable: true,
          cell: (row) => {
              if (row.margin_pct === null || row.margin_pct === undefined) return ''
              const val = Number(row.margin_pct)
              const color = val >= 0 ? 'text-success' : 'text-danger'
              return <span className={color}>{fmtPct(val)}</span>
          }},
        { id: 'break_even_acos', header: 'BE ACoS %', sortable: true,
          cell: (row) => row.break_even_acos != null ? fmtPct(Number(row.break_even_acos)) : '' },
        { id: 'ad_spend_14d', header: `Ad Spend (${adDays}d)`, sortable: true,
          cell: (row) => row.ad_spend_14d != null ? `$${fmtDec(row.ad_spend_14d)}` : '' },
        { id: 'ad_sales_14d', header: `Ad Sales (${adDays}d)`, sortable: true,
          cell: (row) => row.ad_sales_14d != null ? `$${fmtDec(row.ad_sales_14d)}` : '' },
        { id: 'acos', header: 'ACoS', sortable: true,
          cell: (row) => {
              if (row.acos === null || row.acos === undefined) return ''
              const acosVal = fmtPct(Number(row.acos))
              if (row.break_even_acos != null && row.acos != null) {
                  const color = Number(row.acos) <= Number(row.break_even_acos) ? 'text-success' : 'text-danger'
                  return <span className={color}>{acosVal}</span>
              }
              return acosVal
          }},
        { id: 'ad_cost_per_unit', header: 'Ad Cost/Unit', sortable: true,
          cell: (row) => row.ad_cost_per_unit != null ? `$${fmtDec(row.ad_cost_per_unit)}` : '' },
        { id: 'impressions_14d', header: `Impressions (${adDays}d)`, sortable: true,
          cell: (row) => fmtInt(row.impressions_14d) },
        { id: 'clicks_14d', header: `Clicks (${adDays}d)`, sortable: true,
          cell: (row) => fmtInt(row.clicks_14d) },
        { id: 'sessions_14d', header: `Sessions (${adDays}d)`, sortable: true,
          cell: (row) => fmtInt(row.sessions_14d) },
        { id: 'conversion_pct', header: 'Conversion %', sortable: true,
          cell: (row) => fmtPct(row.conversion_pct) },
        { id: 'share_sales_by_ad_pct', header: 'Share by Ad %', sortable: true,
          cell: (row) => fmtPct(row.share_sales_by_ad_pct) },
        { id: 'units_sold_mtd', header: `Units Sold (${data?.rolling_days || 30}d)`, sortable: true,
          cell: (row) => fmtInt(row.units_sold_mtd) },
        { id: 'revenue_mtd', header: `Revenue (${data?.rolling_days || 30}d)`, sortable: true,
          cell: (row) => row.revenue_mtd != null ? `$${fmtDec(row.revenue_mtd)}` : '' },
        { id: 'units_1_10d', header: 'Units 1-10d', sortable: true,
          cell: (row) => fmtInt(row.units_1_10d) },
        { id: 'units_11_20d', header: 'Units 11-20d', sortable: true,
          cell: (row) => fmtInt(row.units_11_20d) },
        { id: 'units_21_30d', header: 'Units 21-30d', sortable: true,
          cell: (row) => fmtInt(row.units_21_30d) },
        { id: 'inventory_qty', header: 'Inventory Qty', sortable: true,
          cell: (row) => fmtInt(row.inventory_qty) },
        { id: 'available_inventory', header: 'Available Inv.', sortable: true,
          cell: (row) => fmtInt(row.available_inventory) },
        { id: 'inventory_level', header: 'Inventory Level', sortable: true,
          cell: (row) => {
              if (!row.inventory_level) return ''
              const variantMap = { 'Out of stock': 'danger', 'Low stock': 'warning', 'Enough': 'success' }
              const variant = variantMap[row.inventory_level] || 'secondary'
              return <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>{row.inventory_level}</Badge>
          }},
        { id: 'sales_level', header: 'Sales Level', sortable: true,
          cell: (row) => {
              if (!row.sales_level) return ''
              const variantMap = { 'Best Seller': 'success', 'Medium Seller': 'primary', 'Slow Seller': 'warning' }
              const variant = variantMap[row.sales_level] || 'secondary'
              return <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>{row.sales_level}</Badge>
          }},
        { id: 'health_status', header: 'Health Status', sortable: true,
          cell: (row) => {
              if (!row.health_status) return ''
              let variant = 'secondary'
              if (row.health_status === 'Healthy') variant = 'success'
              else if (row.health_status === 'At Risk' || row.health_status === 'Stranded') variant = 'warning'
              return <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>{row.health_status}</Badge>
          }},
        { id: 'low_inventory_fee', header: 'Low Inv. Fee', sortable: true,
          cell: (row) => row.low_inventory_fee ? <Badge bg="danger">Applied</Badge> : '' },
    ].filter(col => columnVisibility[col.id])

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="mb-0">Daily Dashboard</h1>
                <div className="d-flex gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => setShowGuide(true)}>
                        <i className="fas fa-info-circle me-1"></i>Column Guide
                    </Button>
                    {backTo && (
                        <Button variant="outline-primary" onClick={() => navigate(backTo)}>
                            <i className="fas fa-arrow-left me-2"></i>{backLabel || 'Back'}
                        </Button>
                    )}
                </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            <div style={{ maxWidth: '900px' }}>
                <div className="d-flex align-items-end gap-3 flex-wrap mb-3">
                    <div style={{ minWidth: '220px', flex: 1 }}>
                        <Form.Label>Amazon Account</Form.Label>
                        <Select
                            value={accountOptions.find(opt => opt.value === selectedAccountId) || null}
                            onChange={(opt) => { setSelectedAccountId(opt?.value || ''); setCurrentPage(1) }}
                            options={accountOptions}
                            isClearable
                            placeholder="Select Account..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div style={{ width: '180px' }}>
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            max={getYesterday()}
                        />
                    </div>
                    <div>
                        <Button variant="primary" onClick={handleLoad}
                            disabled={loading || !selectedAccountId}>
                            {loading ? (<><Spinner size="sm" className="me-2" />Loading...</>) : 'Load'}
                        </Button>
                    </div>
                </div>
            </div>

            {hasLoaded && (
                <>
                    {summary && (
                        <Row className="mb-4 g-3">
                            <Col><div className="bg-primary text-white p-3 rounded text-center"><h5 className="mb-1">Total ASINs</h5><h3 className="mb-0">{summary.total_asins != null ? summary.total_asins.toLocaleString() : ''}</h3></div></Col>
                            <Col><div className="bg-success text-white p-3 rounded text-center"><h5 className="mb-1">Total Revenue - {data?.rolling_days || 30}d ({CHANNEL_LABELS[channel]})</h5><h3 className="mb-0">{formatCurrency(summary.total_revenue)}</h3></div></Col>
                            <Col><div className="bg-warning text-white p-3 rounded text-center"><h5 className="mb-1">Total Ad Spend - {adDays}d</h5><h3 className="mb-0">{formatCurrency(summary.total_ad_spend)}</h3></div></Col>
                            <Col><div className="bg-info text-white p-3 rounded text-center"><h5 className="mb-1">Avg Profit</h5><h3 className="mb-0">{formatCurrency(summary.avg_profit)}</h3></div></Col>
                        </Row>
                    )}

                    {channel !== 'both' && data && data.channel_data_available === false && (
                        <div className="alert alert-warning mb-3">
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            Fulfillment channel data (orders) is not collected for this period. Units Sold and Revenue show both channels.
                        </div>
                    )}

                    {data?.data_freshness && (() => {
                        const f = data.data_freshness
                        const missing = [
                            !f.has_economics && 'Economics',
                            !f.has_traffic && 'Traffic',
                            !f.has_ads && 'Ads',
                            channel !== 'both' && !f.has_orders && 'Orders',
                        ].filter(Boolean)
                        if (missing.length === 0) return null
                        return (
                            <div className="alert alert-danger mb-3">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                <span>Missing {missing.join(', ')} data for {formatDate(selectedDate)}.</span>
                            </div>
                        )
                    })()}

                    <div className="d-flex align-items-center mb-3 gap-3 flex-wrap">
                        <div>{createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}</div>
                        <div className="segmented-control d-inline-flex rounded-2 p-1 gap-1" title="Fulfillment channel for Units Sold and Revenue">
                            {CHANNEL_OPTIONS.map(opt => (
                                <button key={opt.value} type="button"
                                    className={`segmented-control__btn${channel === opt.value ? ' segmented-control__btn--active' : ''}`}
                                    style={{ fontSize: '0.78rem', padding: '3px 12px' }}
                                    onClick={() => { setChannel(opt.value); setCurrentPage(1) }}>
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <div className="segmented-control d-inline-flex rounded-2 p-1 gap-1">
                            {[
                                { value: '', label: 'All' },
                                { value: 'in_stock', label: 'In Stock', dot: 'text-success' },
                                { value: 'low_and_out', label: 'Low & Out', dot: 'text-warning' },
                                { value: 'out_of_stock', label: 'Out of Stock', dot: 'text-danger' },
                            ].map(opt => (
                                <button key={opt.value} type="button"
                                    className={`segmented-control__btn${inventoryFilter === opt.value ? ' segmented-control__btn--active' : ''}`}
                                    style={{ fontSize: '0.78rem', padding: '3px 12px' }}
                                    onClick={() => { setInventoryFilter(opt.value); setCurrentPage(1) }}>
                                    {opt.dot && <span className={`${opt.dot} me-1`} style={{ fontSize: '0.6rem' }}>●</span>}
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <Form.Check type="checkbox" label="Exclude items with no activity"
                            checked={excludeInactive} onChange={(e) => { setExcludeInactive(e.target.checked); setCurrentPage(1) }}
                            style={{ fontSize: '0.82rem' }} />
                        <div className="ms-auto" style={{ maxWidth: '300px' }}>
                            <SearchInputWithClear value={searchInputValue} onChange={handleSearch}
                                onClear={handleClearSearch} placeholder="Search ASIN, name, or SKU..." />
                        </div>
                    </div>

                    {loading && rows.length === 0 ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : rows.length === 0 ? (
                        <p className="text-muted">No data found for the selected date.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <ResizableTable columns={dashboardColumns} data={rows} tableId="amazon_daily_dashboard"
                                    enableSorting={true} onSort={handleSort}
                                    sortColumn={sortColumn} sortDirection={sortDirection}
                                    initialColumnWidths={{
                                        asin: 130, amazon_sku: 110, product_name: 250,
                                        sale_price: 90, fba_fee: 80, referral_fee: 90, inbound_fee: 90,
                                        storage_fee: 90, cogs: 70, profit: 80, margin_pct: 80,
                                        break_even_acos: 95,
                                        ad_spend_14d: 110, ad_sales_14d: 110, acos: 70,
                                        ad_cost_per_unit: 100,
                                        impressions_14d: 120, clicks_14d: 90, sessions_14d: 100,
                                        conversion_pct: 110, share_sales_by_ad_pct: 110,
                                        units_sold_mtd: 120, revenue_mtd: 110,
                                        inventory_qty: 100, available_inventory: 110,
                                        inventory_level: 120,
                                        health_status: 120, low_inventory_fee: 100,
                                    }}
                                    striped hover className="table-sm" />
                            </div>
                            <div className="mt-3">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={setPageSize}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
            <DailyDashboardGuideModal show={showGuide} onHide={() => setShowGuide(false)} />
        </Container>
    )
}

export default AmazonDailyDashboardScreen
